import React, { useState, useRef } from 'react'
import { useFloating, offset, flip, shift, autoUpdate, useClick, useDismiss, useRole, useInteractions, FloatingPortal, FloatingFocusManager } from '@floating-ui/react'

export function MessageMenu({ placement = 'bottom-end', isActionAllowed, isAdminUser, reportAbuseOption, isOwnMessage, hasAttachments, isReportedByMe, isPendingApproval, messageTime, allowedDuration, allowPersonalChat, hasUrl, ...handlers }) {
  const exactTimeRef = useRef(null)
  const canEdit = ((isActionAllowed && !isAdminUser) || (isOwnMessage && isAdminUser)) && exactTimeRef.current - messageTime <= allowedDuration
  const canReplyOrForward = !isPendingApproval

  const [open, setOpen] = useState(false)
  const deleteForEveryone = exactTimeRef.current - messageTime <= allowedDuration

  const { refs, context, x, y, strategy } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    strategy: 'fixed',
    middleware: [
      offset({ mainAxis: 4, crossAxis: 6 }), // ⬅️ gap downwards + sideways
      flip(),
      shift({ padding: 8 })
    ],
    whileElementsMounted: autoUpdate
  })

  const click = useClick(context)
  const dismiss = useDismiss(context, { outsidePressEvent: 'mousedown' })
  const role = useRole(context, { role: 'menu' })
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role])

  const close = () => setOpen(false)
  const Item = ({ icon, label, onClick, danger, disabled }) => (
    <button
      className={`msg-menu__item${danger ? ' danger' : ''}${disabled ? ' disabled' : ''}`}
      role="menuitem"
      disabled={disabled}
      onClick={() => {
        if (!disabled && onClick) {
          onClick()
          close()
        }
      }}
      style={{ opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
    >
      <i className={`pi ${icon} ${label === 'Reply' ? 'reply-icon' : ''}`} aria-hidden="true" />
      <span>{label}</span>
    </button>
  )

  const handleClick = () => {
    exactTimeRef.current = new Date()
  }

  return (
    <>
      <button
        ref={refs.setReference}
        {...getReferenceProps({
          type: 'button',
          className: 'msg-menu-trigger',
          'aria-label': 'Message options',
          onClick: handleClick
        })}
      >
        <i className="pi pi-chevron-down" />
      </button>

      <FloatingPortal>
        {open && (
          <FloatingFocusManager context={context} modal={false} initialFocus={-1}>
            <div
              ref={refs.setFloating}
              {...getFloatingProps({
                className: 'msg-menu',
                style: {
                  position: strategy,
                  top: y ?? 0,
                  left: x ?? 0,
                  visibility: x == null ? 'hidden' : 'visible'
                }
              })}
            >
              <Item icon="pi-reply" style={{ transform: 'scaleX(-1)' }} label="Reply" onClick={canReplyOrForward ? handlers.onReply : undefined} disabled={!canReplyOrForward} />
              <Item icon="pi-reply" label="Forward" onClick={canReplyOrForward ? handlers.onForward : undefined} disabled={!canReplyOrForward} />
              {/* {isAdmin && !isOwnMessage && <Item icon="pi-ban" danger label="Block" onClick={handlers.onBlock} />} */}
              {/* {isActionAllowed && !isAdminUser && <Item icon="pi-pencil" label="Edit" onClick={handlers.onEdit} />}
              {isOwnMessage && isAdminUser && <Item icon="pi-pencil" label="Edit" onClick={handlers.onEdit} />} */}
              {canEdit && hasAttachments && <Item icon="pi-pencil" label="Edit" onClick={canReplyOrForward ? handlers.onEdit : undefined} disabled={!canReplyOrForward || hasUrl} />}
              {!isOwnMessage && allowPersonalChat && <Item icon="pi-envelope" label="Start Personal Chat" onClick={handlers.onChatPersonally} />}

              {/* <Item icon="pi-copy" label="Copy" onClick={handlers.onCopy} /> */}
              {/* <Item icon="pi-face-smile" label="React" onClick={handlers.onReact} /> */}
              {/* <Item icon="pi-arrow-right" label="Forward" onClick={handlers.onForward} /> */}
              {/* <Item icon="pi-thumbtack" label="Pin" onClick={handlers.onPin} /> */}
              {/* <Item icon="pi-star" label="Star" onClick={handlers.onStar} /> */}
              {/* <Item icon="pi-plus-circle" label="Add text to note" onClick={handlers.onAddNote} /> */}

              <div className="msg-menu__separator" />
              {!isAdminUser && <Item icon="pi-trash" danger label="Delete For Me" onClick={handlers.onDeleteForMe} />}
              {deleteForEveryone && isActionAllowed && !isAdminUser && isOwnMessage && <Item icon="pi-trash" danger label="Delete for Everyone" onClick={handlers.onDeleteForEveryone} />}
              {/* <Item icon="pi-flag" label="Report" onClick={handlers.onReport} /> */}
              {isAdminUser && <Item icon="pi-trash" danger label="Delete Message" onClick={handlers.onDeleteMessageByAdmin} />}
              {isAdminUser && !isOwnMessage && <Item icon="pi-trash" danger label="Delete All Message of User" onClick={handlers.onDeleteAllMessageByAdmin} />}
              {isAdminUser && !isOwnMessage && <Item icon="pi-ban" danger label="Block User" onClick={handlers.onBlockUserByAdmin} />}

              {!isOwnMessage && !reportAbuseOption && !isAdminUser && (isReportedByMe ? <Item icon="pi-undo" danger label="Undo Report" onClick={handlers.onUndoReportAbuse} /> : <Item icon="pi-flag" danger label="Report Abuse" onClick={handlers.onReportAbuse} />)}

              {/* {!isOwnMessage && !reportAbuseOption && !isAdminUser && <Item icon="pi-flag" danger label="Report Abuse" onClick={handlers.onReportAbuse} />} */}
              {/* <Item icon="pi-trash" label="Delete" danger onClick={handlers.onDelete} /> */}
            </div>
          </FloatingFocusManager>
        )}
      </FloatingPortal>
    </>
  )
}
